import jwt_decode from 'jwt-decode';
import { Injectable } from '@angular/core';
import { AuthUtils } from 'app/auth/auth.utils';
import { HttpClient } from '@angular/common/http';
import { UserService } from 'app/user/user.service';
import { catchError, map, Observable, of, switchMap, throwError } from 'rxjs';
import { ApiService } from 'app/shared/constant/api-service/api.service';
import { API_ENDPOINTS } from 'app/core/constants/api-endpoints.constant';
import { environment } from 'environments/environment';

@Injectable()
export class AuthService {
    private tokenKey = 'token';
    private idtokenkey = 'idtoken';
    private _authenticated: boolean = false;

    header = {
        headers: new Headers({
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + this.accessToken,
        }),
        responseType: 'text',
    };

    constructor(
        private _httpClient: HttpClient,
        private _userService: UserService,
        private _apiService: ApiService,
    ) {}

    set accessToken(token: string) {
        localStorage.setItem('accessToken', token);
    }

    get accessToken(): string {
        return localStorage.getItem('accessToken') ?? '';
    }

    /**
     * Reset password
     *
     * @param password
     */
    resetPassword(password: string): Observable<any> {
        return this._httpClient.post('api/auth/reset-password', password);
    }

    /**
     * Sign in
     *
     * @param credentials
     */
    signIn(credentials: { email: string; password: string }): Observable<any> {
        // Throw error, if the user is already logged in
        if (this._authenticated) {
            return throwError('User is already logged in.');
        }

        return this._httpClient.post('api/auth/sign-in', credentials).pipe(
            switchMap((response: any) => {
                // Store the access token in the local storage
                this.accessToken = response.accessToken;

                // Set the authenticated flag to true
                this._authenticated = true;

                // Store the user on the user service
                this._userService.user = response.user;

                // Return a new observable with the response
                return of(response);
            }),
        );
    }

    /**
     * Sign in using the access token
     */
    signInUsingToken(): Observable<any> {
        // Sign in using the token
        return this._httpClient
            .post('api/auth/sign-in-with-token', {
                accessToken: this.accessToken,
            })
            .pipe(
                catchError(() =>
                    // Return false
                    of(false),
                ),
                switchMap((response: any) => {
                    // Replace the access token with the new one if it's available on
                    // the response object.
                    //
                    // This is an added optional step for better security. Once you sign
                    // in using the token, you should generate a new one on the server
                    // side and attach it to the response object. Then the following
                    // piece of code can replace the token with the refreshed one.
                    if (response.accessToken) {
                        this.accessToken = response.accessToken;
                    }

                    // Set the authenticated flag to true
                    this._authenticated = true;

                    // Store the user on the user service
                    this._userService.user = response.user;

                    // Return true
                    return of(true);
                }),
            );
    }

    signOut() {
        // Notify iDA iframe to clear auth token before localStorage is wiped
        try {
            const iframe = window.document.querySelector(
                '.chat-wrapper iframe',
            ) as HTMLIFrameElement;
            if (iframe?.contentWindow) {
                iframe.contentWindow.postMessage(
                    { type: 'CLEAR_AUTH_TOKEN' },
                    new URL(environment.CHATBOT_URL).origin,
                );
            }
        } catch {}

        this._apiService
            .post(`${API_ENDPOINTS.LOGOUT}`, { refreshToken: '' })
            .subscribe((res: any) => {});
        // Set the authenticated flag to false
        // this._httpClient.post('http://api.dev.pave.com.my/auth/imsme/logout', {refreshToken: localStorage.getItem('refreshToken')}).subscribe(
        //     (res:any)=>{
        //     }
        // );
        localStorage.clear();
        sessionStorage.removeItem('utm_id');
        sessionStorage.removeItem('utm_source');
        sessionStorage.removeItem(this.idtokenkey);
        sessionStorage.removeItem(this.tokenKey);
        location.href = '/login';
        this.broadcastLogout();
        this._authenticated = false;
    }

    broadcastLogout(): void {
        localStorage.setItem('logout-event', String(Date.now()));
    }

    signUp(user: {
        name: string;
        email: string;
        password: string;
        company: string;
    }): Observable<any> {
        return this._httpClient.post('api/auth/sign-up', user);
    }

    userSignUp(userDetails): Observable<any> {
        return this._apiService.post(`${API_ENDPOINTS.SIGN_UP}`, userDetails);
    }

    confirmUserSignUp(confirmDetails): Observable<any> {
        return this._apiService.post(
            `${API_ENDPOINTS.CONFIRM_SIGN_UP}`,
            confirmDetails,
        );
    }

    resendSignUpEmail(resendDetails): Observable<any> {
        return this._apiService.post(
            `${API_ENDPOINTS.RESEND_SIGN_UP_EMAIL}`,
            resendDetails,
        );
    }

    sendVerificationEmail(sendDetails): Observable<any> {
        return this._apiService.post(
            `${API_ENDPOINTS.SEND_VERIFICATION_EMAIL}`,
            sendDetails,
        );
    }

    userLogin(loginDetails): Observable<any> {
        return this._apiService.post(`${API_ENDPOINTS.LOGIN}`, loginDetails);
    }

    getToken() {
        return this.getCookie(this.tokenKey);
    }

    getIdToken() {
        return this.getCookie(this.idtokenkey);
    }

    getCookie(cname): string | null {
        let name = cname + '=';
        let decodedCookie = decodeURIComponent(document.cookie);
        let ca = decodedCookie.split(';');
        for (let i = 0; i < ca.length; i++) {
            let c = ca[i];
            while (c.charAt(0) == ' ') {
                c = c.substring(1);
            }
            if (c.indexOf(name) == 0) {
                return c.substring(name.length, c.length);
            }
        }
        return null;
    }

    forgotPassword(userDetails): Observable<any> {
        return this._apiService.post(
            `${API_ENDPOINTS.FORGOT_PASSWORD}`,
            userDetails,
        );
    }

    confirmForgotPassword(userDetails): Observable<any> {
        return this._apiService.post(
            `${API_ENDPOINTS.CONFIRM_FORGOT_PASSWORD}`,
            userDetails,
        );
    }

    /**
     * Unlock session
     *
     * @param credentials
     */
    unlockSession(credentials: {
        email: string;
        password: string;
    }): Observable<any> {
        return this._httpClient.post('api/auth/unlock-session', credentials);
    }

    check(): Observable<boolean> {
        // Check if the user is logged in
        if (this._authenticated) {
            return of(true);
        }

        // Check the access token availability
        if (!this.accessToken) {
            return of(false);
        }

        // Check the access token expire date
        if (AuthUtils.isTokenExpired(this.accessToken)) {
            return of(false);
        }

        // If the access token exists and it didn't expire, sign in using it
        return this.signInUsingToken();
    }

    isLoggedIn(): boolean {
        if (localStorage.getItem('accessToken')) {
            return true;
        }
        return false;
    }

    isTokenExpired(): boolean {
        let token = this.getToken();
        if (!token) return false;
        let decoded = jwt_decode(token);
        let expireTime = new Date(decoded['exp'] * 1800);
        return expireTime < new Date();
    }

    isTokenExpiredParam(token: string): boolean {
        if (!token) return false;
        let decoded = jwt_decode(token);
        let expireTime = new Date(decoded['exp'] * 1800);

        return expireTime < new Date();
    }

    isIdTokenExpired(): boolean {
        let token = this.getIdToken();
        if (!token) return false;
        let decoded = jwt_decode(token);
        let expireTime = new Date(decoded['exp'] * 1800);

        return expireTime < new Date();
    }

    generateSmsOTP(data): Observable<any> {
        return this._apiService.post(
            API_ENDPOINTS.IMSME_GENERATE_SMS_OTP,
            data,
            this.header,
        );
    }

    verifySmsOTP(data): Observable<any> {
        return this._apiService.post(
            API_ENDPOINTS.IMSME_VERIFY_SMS_OTP,
            data,
            this.header,
        );
    }

    updateProfile(data: any, options?: any): Observable<any> {
        return this._apiService.post(API_ENDPOINTS.IMSME_UPDATE_PROFILE, data);
    }

    updatePassword(data: any, options?: any): Observable<any> {
        return this._apiService.post(API_ENDPOINTS.IMSME_CHANGE_PASSWORD, data);
    }

    isRefreshTokenExpired(): boolean {
        const refreshToken = this.getRefreshToken();
        if (!refreshToken) return true; // If there's no refresh token, consider it expired
    }

    // Add this method to AuthService
    updateAccessToken(newAccessToken: string) {
        this.accessToken = newAccessToken;
    }

    refreshAccessToken(): Observable<string> {
        const refreshToken = this.getRefreshToken();

        if (!refreshToken) {
            return throwError('No refresh token available');
        }

        // Make a request to your server to refresh the token
        return this._httpClient
            .post<any>(API_ENDPOINTS.BASE_URL + API_ENDPOINTS.REFRESH_TOKEN, {
                refreshToken,
                username: this._userService.getUserFromLocal().username,
            })
            .pipe(
                map((response) => {
                    localStorage.setItem('idToken', response.idToken);
                    const newAccessToken = response.accessToken;
                    this.updateAccessToken(newAccessToken); // Update access token here
                    return newAccessToken;
                }),
            );
    }

    getRefreshToken(): string | null {
        return localStorage.getItem('refreshToken');
    }

    getIdTokenLocal() {
        return localStorage.getItem('idToken');
    }
}
